import { useState, useEffect } from "react";
import { api } from "../api/client";
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Alert,
  Card,
  CardContent,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "@mui/material";

export default function RolloutWizard() {
  const [version, setVersion] = useState("");
  const [apkUrl, setApkUrl] = useState("");
  const [flavor, setFlavor] = useState("all");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [activeRollout, setActiveRollout] = useState(null);
  const [loading, setLoading] = useState(false);

  async function fetchActiveRollout() {
    try {
      const response = await api("/admin/rollout/active");
      setActiveRollout(response.data);
    } catch (err) {
      // No active rollout is not an error
      if (!err.message?.includes("No active rollout")) {
        console.error("Failed to fetch active rollout:", err);
      }
      setActiveRollout(null);
    }
  }

  useEffect(() => {
    fetchActiveRollout();
    const interval = setInterval(fetchActiveRollout, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, []);

  async function launch() {
    setStatus("Launching APK rollout...");
    setError(null);
    setLoading(true);
    try {
      await api("/admin/rollout", {
        method: "POST",
        body: JSON.stringify({ version, apkUrl, flavor })
      });
      setStatus(`APK rollout launched successfully for ${flavor} devices!`);
      setVersion("");
      setApkUrl("");
      setFlavor("all");
      await fetchActiveRollout();
    } catch (err) {
      setError(err.message || "Failed to launch rollout");
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }

  async function cancelRollout(rolloutId) {
    setLoading(true);
    setError(null);
    try {
      await api(`/admin/rollout/${rolloutId}/cancel`, {
        method: "POST"
      });
      setStatus("Rollout cancelled successfully");
      await fetchActiveRollout();
    } catch (err) {
      setError(err.message || "Failed to cancel rollout");
    } finally {
      setLoading(false);
    }
  }

  async function forceUpdate(rolloutId) {
    setLoading(true);
    setError(null);
    try {
      await api(`/admin/rollout/${rolloutId}/force-update`, {
        method: "POST"
      });
      setStatus("Force update triggered - devices will update on next heartbeat");
      await fetchActiveRollout();
    } catch (err) {
      setError(err.message || "Failed to trigger force update");
    } finally {
      setLoading(false);
    }
  }

  const getFlavorColor = (f) => {
    switch(f) {
      case 'arrivals': return 'info';
      case 'departures': return 'warning';
      case 'all': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h6" mb={1}>APK Rollout</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Configure and launch APK updates to fleet devices. You can target specific flavors (Arrivals/Departures) or all devices.
      </Typography>

      {/* Active Rollout Card */}
      {activeRollout && (
        <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <Typography variant="h6">Active Rollout</Typography>
              <Chip
                label={activeRollout.status}
                color={activeRollout.status === 'pending' ? 'warning' : 'info'}
                size="small"
              />
              <Chip
                label={activeRollout.flavor}
                color={getFlavorColor(activeRollout.flavor)}
                size="small"
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Version:</strong> {activeRollout.version}
              </Typography>
              <Typography variant="body2">
                <strong>Flavor:</strong> {activeRollout.flavor}
              </Typography>
              <Typography variant="body2">
                <strong>Target Devices:</strong> {activeRollout.targetDevices?.length || 0} devices ({activeRollout.targetDevices?.length === 0 ? 'All active devices' : activeRollout.targetDevices?.join(', ')})
              </Typography>
              <Typography variant="body2">
                <strong>Progress:</strong> {activeRollout.progress?.updated || 0} / {activeRollout.progress?.total || 0} updated
                {activeRollout.progress?.failed > 0 && `, ${activeRollout.progress.failed} failed`}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <strong>APK URL:</strong> {activeRollout.apkUrl}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => forceUpdate(activeRollout._id)}
                disabled={loading || activeRollout.status === 'completed' || activeRollout.status === 'cancelled'}
              >
                Force Update Now
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => cancelRollout(activeRollout._id)}
                disabled={loading || activeRollout.status === 'completed' || activeRollout.status === 'cancelled'}
              >
                Cancel Rollout
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* New Rollout Form */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" mb={2}>Launch New Rollout</Typography>
          <Stack spacing={2} maxWidth={600}>
            <Stack spacing={2} direction="row">
              <TextField
                label="Version"
                value={version}
                onChange={e => setVersion(e.target.value)}
                placeholder="e.g., 0.1.5"
                size="small"
                sx={{ width: 150 }}
              />
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel>Target Flavor</InputLabel>
                <Select
                  value={flavor}
                  label="Target Flavor"
                  onChange={e => setFlavor(e.target.value)}
                >
                  <MenuItem value="all">All Devices</MenuItem>
                  <MenuItem value="arrivals">Arrivals Only</MenuItem>
                  <MenuItem value="departures">Departures Only</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <TextField
              label="APK URL"
              value={apkUrl}
              onChange={e => setApkUrl(e.target.value)}
              placeholder="https://api.fids.eximenon.com/uploads/apks/app-departures-0.1.5.apk"
              size="small"
              fullWidth
              helperText="Direct HTTPS URL to the APK file"
            />
            <Button
              variant="contained"
              onClick={launch}
              disabled={!version || !apkUrl || loading || activeRollout}
              sx={{ alignSelf: 'flex-start' }}
            >
              {loading ? 'Launching...' : 'Launch APK Rollout'}
            </Button>
            {activeRollout && (
              <Alert severity="warning">
                Cannot launch a new rollout while one is active. Please cancel or wait for the active rollout to complete.
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Status Messages */}
      <Box mt={2}>
        {status && <Alert severity="success" onClose={() => setStatus(null)}>{status}</Alert>}
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      </Box>
    </Box>
  );
}
